export type MoonPhase =
    | 'New Moon'
    | 'Waxing Crescent'
    | 'First Quarter'
    | 'Waxing Gibbous'
    | 'Full Moon'
    | 'Waning Gibbous'
    | 'Last Quarter'
    | 'Waning Crescent';

export interface MoonData {
    phase: MoonPhase;
    illumination: number; // 0 to 1
    age: number; // days since new moon
    meaning: string;
    keywords: string[];
}

const MOON_CYCLE_DAYS = 29.53058867;

export function getMoonData(date: Date): MoonData {
    const knownNewMoon = new Date('2000-01-06T18:14:00Z'); // A known new moon reference date
    const diffTime = date.getTime() - knownNewMoon.getTime();
    const diffDays = diffTime / (1000 * 60 * 60 * 24);
    const totalCycles = diffDays / MOON_CYCLE_DAYS;
    const currentCycle = totalCycles - Math.floor(totalCycles);
    const age = currentCycle * MOON_CYCLE_DAYS;

    let phase: MoonPhase;
    let meaning: string;
    let keywords: string[];

    // Determine phase based on age (approximate)
    if (age < 1.84566) {
        phase = 'New Moon';
        meaning = "Born under the New Moon, you are a pioneer and an initiator. You have a strong sense of potential and are often starting new chapters in life. You are intuitive, spontaneous, and full of new ideas.";
        keywords = ['New Beginnings', 'Potential', 'Intuition', 'Spontaneity'];
    } else if (age < 5.53699) {
        phase = 'Waxing Crescent';
        meaning = "Born under the Waxing Crescent, you are a builder and a dreamer. You have the drive to turn your intentions into reality. You are often action-oriented and eager to learn and grow.";
        keywords = ['Growth', 'Action', 'Learning', 'Ambition'];
    } else if (age < 9.22831) {
        phase = 'First Quarter';
        meaning = "Born under the First Quarter Moon, you are a warrior and a decision-maker. You face challenges head-on and are not afraid to make tough choices. You are strong-willed and resilient.";
        keywords = ['Strength', 'Decisions', 'Resilience', 'Action'];
    } else if (age < 12.91963) {
        phase = 'Waxing Gibbous';
        meaning = "Born under the Waxing Gibbous, you are a perfectionist and a refiner. You are constantly analyzing and improving yourself and your surroundings. You have a keen eye for detail and a desire for excellence.";
        keywords = ['Refinement', 'Analysis', 'Improvement', 'Detail'];
    } else if (age < 16.61096) {
        phase = 'Full Moon';
        meaning = "Born under the Full Moon, you are a radiant and expressive soul. You wear your heart on your sleeve and have a magnetic personality. You are often the center of attention and have a strong desire to connect with others.";
        keywords = ['Fulfillment', 'Expression', 'Connection', 'Radiance'];
    } else if (age < 20.30228) {
        phase = 'Waning Gibbous';
        meaning = "Born under the Waning Gibbous, you are a teacher and a disseminator. You have a wealth of knowledge and a desire to share it with the world. You are often wise beyond your years and have a strong sense of purpose.";
        keywords = ['Wisdom', 'Sharing', 'Purpose', 'Teaching'];
    } else if (age < 23.99361) {
        phase = 'Last Quarter';
        meaning = "Born under the Last Quarter Moon, you are a revolutionary and a transformer. You are often letting go of the old to make way for the new. You are independent and have a unique perspective on life.";
        keywords = ['Transformation', 'Release', 'Independence', 'Change'];
    } else {
        phase = 'Waning Crescent';
        meaning = "Born under the Waning Crescent, you are a mystic and a sage. You have a deep connection to the spiritual world and a strong intuition. You are often reflective and have a rich inner life.";
        keywords = ['Reflection', 'Spirituality', 'Intuition', 'Closure'];
    }

    // Calculate illumination (simplified)
    const illumination = 0.5 * (1 - Math.cos((age / MOON_CYCLE_DAYS) * 2 * Math.PI));

    return {
        phase,
        illumination,
        age,
        meaning,
        keywords
    };
}
